// Procedural sound — no audio files. A low room drone, a steam hiss, and a
// heartbeat that quickens with dread, plus stingers for appearances and death.
// Everything is synthesised with the Web Audio API and gated behind the first
// user gesture (browser autoplay policy).

export class GameAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private droneGain: GainNode | null = null;
  private hissGain: GainNode | null = null;
  private whisperGain: GainNode | null = null;
  private whispering = false;
  private dread = 0;
  private heartTimer = 0;
  private started = false;

  /** Wire a one-shot gesture listener; audio boots on the first click/key/touch. */
  armOnFirstGesture(onReady?: () => void) {
    const boot = () => {
      this.start();
      onReady?.();
      window.removeEventListener("pointerdown", boot);
      window.removeEventListener("keydown", boot);
      window.removeEventListener("touchstart", boot);
    };
    window.addEventListener("pointerdown", boot);
    window.addEventListener("keydown", boot);
    window.addEventListener("touchstart", boot);
  }

  start() {
    if (this.started) return;
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.started = true;
    const ctx = this.ctx;

    this.master = ctx.createGain();
    this.master.gain.value = 0.9;
    this.master.connect(ctx.destination);

    // Sub drone — two detuned low oscillators.
    this.droneGain = ctx.createGain();
    this.droneGain.gain.value = 0.05;
    this.droneGain.connect(this.master);
    for (const f of [47, 47.5, 70.5]) {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = f;
      o.connect(this.droneGain);
      o.start();
    }

    // Steam hiss — looping filtered white noise.
    const noise = this.noiseBuffer(ctx, 2);
    const src = ctx.createBufferSource();
    src.buffer = noise;
    src.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1400;
    bp.Q.value = 0.6;
    this.hissGain = ctx.createGain();
    this.hissGain.gain.value = 0.015;
    src.connect(bp);
    bp.connect(this.hissGain);
    this.hissGain.connect(this.master);
    src.start();

    // Whisper bed — narrow-band noise warbled by a slow LFO, so it breathes and
    // sibilates like words half-heard at your ear. Silent until setWhispers(true).
    const wnoise = this.noiseBuffer(ctx, 3);
    const wsrc = ctx.createBufferSource();
    wsrc.buffer = wnoise;
    wsrc.loop = true;
    const wbp = ctx.createBiquadFilter();
    wbp.type = "bandpass";
    wbp.frequency.value = 2200; // sibilant "sss/shh" band
    wbp.Q.value = 4;
    this.whisperGain = ctx.createGain();
    this.whisperGain.gain.value = 0;
    // LFO gating the whisper amplitude → the cadence of breath/speech.
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 2.7;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.5;
    const lfoBias = ctx.createConstantSource();
    lfoBias.offset.value = 0.5;
    lfo.connect(lfoGain);
    // amplitude = bias + lfo, driving an inner gain node
    const innerWhisper = ctx.createGain();
    innerWhisper.gain.value = 0;
    lfoGain.connect(innerWhisper.gain);
    lfoBias.connect(innerWhisper.gain);
    wsrc.connect(wbp);
    wbp.connect(innerWhisper);
    innerWhisper.connect(this.whisperGain);
    this.whisperGain.connect(this.master);
    wsrc.start();
    lfo.start();
    lfoBias.start();

    this.scheduleHeart();
  }

  /** Toggle the at-the-ear whisper bed (the "voices" effect). Fades in/out. */
  setWhispers(on: boolean) {
    if (on === this.whispering) return;
    this.whispering = on;
    if (!this.ctx || !this.whisperGain) return;
    const t = this.ctx.currentTime;
    this.whisperGain.gain.setTargetAtTime(on ? 0.06 : 0, t, 0.6);
  }

  /** Call each frame/poll. Dread (0..1) thickens the drone, hiss and heartbeat. */
  setDread(d: number) {
    this.dread = Math.max(0, Math.min(1, d));
    if (!this.ctx || !this.droneGain || !this.hissGain) return;
    const t = this.ctx.currentTime;
    this.droneGain.gain.setTargetAtTime(0.05 + this.dread * 0.16, t, 0.5);
    this.hissGain.gain.setTargetAtTime(0.015 + this.dread * 0.05, t, 0.5);
  }

  /** A presence appeared — a dissonant rising swell. */
  appearStinger() {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const g = ctx.createGain();
    g.gain.value = 0;
    g.connect(this.master);
    for (const f of [110, 116.5, 220]) {
      const o = ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.value = f;
      o.connect(g);
      o.start();
      o.stop(ctx.currentTime + 1.6);
    }
    g.gain.setValueAtTime(0, ctx.currentTime);
    g.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 1.2);
    g.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.6);
  }

  /** Death — a loud burst and a downward sweep. */
  deathStinger() {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const g = ctx.createGain();
    g.gain.value = 0.0001;
    g.connect(this.master);
    const o = ctx.createOscillator();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(420, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.9);
    o.connect(g);
    // burst of noise
    const ns = ctx.createBufferSource();
    ns.buffer = this.noiseBuffer(ctx, 0.5);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.5, ctx.currentTime);
    ng.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    ns.connect(ng);
    ng.connect(this.master);
    o.start();
    ns.start();
    g.gain.setValueAtTime(0.4, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.9);
    o.stop(ctx.currentTime + 0.95);
  }

  private scheduleHeart() {
    if (!this.ctx) return;
    const beat = () => {
      if (!this.ctx || !this.master) return;
      if (this.dread > 0.12) this.thump();
      // Faster as dread climbs: ~1.4s calm → ~0.45s terrified.
      const interval = 1400 - this.dread * 950;
      this.heartTimer = window.setTimeout(beat, interval);
    };
    beat();
  }
  private thump() {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    g.connect(this.master!);
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(60, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(35, ctx.currentTime + 0.16);
    o.connect(g);
    const vol = 0.12 + this.dread * 0.25;
    g.gain.setValueAtTime(vol, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);
    o.start();
    o.stop(ctx.currentTime + 0.24);
  }

  private noiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
    const len = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    return buf;
  }
}
